import React from "react";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Card,
  type Color,
  EntityChipShell,
  Icon,
  type IconName,
  Image,
  Text,
} from "@salestrak/ui";

/** One segment of a config-panel breadcrumb. `onClick` navigates back to it; the
 * last segment is always the current page and is never a link. */
export type ConfigCrumbSegment = {
  label: string;
  icon: IconName;
  /** Full-color brand logo tile for this segment; falls back to `icon`. */
  logo?: string | undefined;
  onClick?: (() => void) | undefined;
};

/** The 16px brand-logo tile rendered in place of a lucide icon at glyph size —
 *  a crumb segment, and a Source dropdown row on the automation surfaces. Sized
 *  to the standard 16px glyph box so it aligns with the icon slot beside it. */
export function BrandLogoTile({ logo }: { logo: string }) {
  return (
    <Box height={16} overflow="hidden" rounded="sm" width={16}>
      <Image alt="" fill src={logo} />
    </Box>
  );
}

/** Breadcrumb above a config panel: the trail the user came down (each earlier
 * segment clickable → back to it) ending in the current page. Shared by the
 * provider config flow; placed in the desktop top bar and the
 * mobile sheet header. Segments are emitted as a flat array (no Fragments) so
 * `BreadcrumbList` can still detect that the first segment is a link and cancel
 * its padding against the parent's content edge. */
export function ConfigCrumb({ trail }: { trail: ConfigCrumbSegment[] }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {trail.flatMap((segment, index) => {
          const leading = segment.logo ? (
            <BrandLogoTile logo={segment.logo} />
          ) : (
            <Icon color="muted" name={segment.icon} size="sm" />
          );
          const item = (
            <BreadcrumbItem key={`${segment.label}-${index}`}>
              {index === trail.length - 1 ? (
                <BreadcrumbPage>
                  <Box align="center" direction="row" gap="2xs">
                    {leading}
                    <Text size="sm" truncate={1}>
                      {segment.label}
                    </Text>
                  </Box>
                </BreadcrumbPage>
              ) : (
                // The shell borrows the ghost-button geometry (same idiom as
                // EntityBreadcrumbLink) so a clickable crumb reads as one.
                <EntityChipShell as="box" size="xs" onClick={segment.onClick}>
                  {leading}
                  <Text size="sm" truncate={1} variant="muted">
                    {segment.label}
                  </Text>
                </EntityChipShell>
              )}
            </BreadcrumbItem>
          );
          return index === 0
            ? [item]
            : [<BreadcrumbSeparator key={`separator-${index}`} />, item];
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/** Rounded chip — shared by config headers and option rows. Renders a provider's
 * full-color brand `logo` tile when given; otherwise a muted box with the lucide
 * `icon` tinted by `color`. The logo tile bakes its own rounded corners (matching
 * `rounded="md"`), so it fills the chip and the muted background never shows. */
export function IconChip({
  name,
  size = "sm",
  color = "muted",
  logo,
}: {
  name: IconName;
  size?: "sm" | "lg" | undefined;
  color?: Color | undefined;
  logo?: string | undefined;
}) {
  const dimension = size === "lg" ? 44 : 36;
  if (logo) {
    return (
      <Box height={dimension} overflow="hidden" rounded="md" width={dimension}>
        <Image alt="" fill src={logo} />
      </Box>
    );
  }
  return (
    <Box
      align="center"
      background="muted"
      height={dimension}
      justify="center"
      rounded="md"
      width={dimension}
    >
      <Icon color={color} name={name} size={size === "lg" ? "lg" : "md"} />
    </Box>
  );
}

/** A non-collapsible labeled group of option rows. The "General"/"Enable"
 * groups pass no label, matching the design where their heading is hidden. */
export function ConfigSection({
  label,
  children,
}: {
  label?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <Box fill="width" gap="sm">
      {label ? (
        <Text size="sm" weight="semibold">
          {label}
        </Text>
      ) : null}
      <Box fill="width" gap="sm">
        {children}
      </Box>
    </Box>
  );
}

/** One option row: icon chip + title/description, with a trailing control
 * (Switch / Button) or pressable (command launcher). */
export function OptionRow({
  icon,
  iconColor,
  logo,
  title,
  description,
  trailing,
  onClick,
}: {
  icon: IconName;
  iconColor?: Color | undefined;
  /** Full-color brand logo tile; falls back to `icon` when absent. */
  logo?: string | undefined;
  title: string;
  description: string;
  trailing?: React.ReactNode | undefined;
  onClick?: (() => void) | undefined;
}) {
  return (
    <Card
      align="center"
      cursor={onClick ? "pointer" : undefined}
      direction="row"
      fill="width"
      gap="md"
      hoverBackground={onClick ? true : undefined}
      padding="md"
      radius="lg"
      onClick={onClick}
    >
      <IconChip color={iconColor} logo={logo} name={icon} />
      <Box fill gap="2xs">
        <Text size="sm" weight="semibold">
          {title}
        </Text>
        <Text size="sm" variant="muted">
          {description}
        </Text>
      </Box>
      {trailing}
    </Card>
  );
}
